import React, { useState, useEffect } from "react";
import "./DatabaseBackup.css";
import { backend_url } from "../../App";

const DatabaseBackup = () => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState("");
  const [restoreStatus, setRestoreStatus] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [backupInfo, setBackupInfo] = useState(null);
  const [lastBackup, setLastBackup] = useState(null);
  const [lastRestore, setLastRestore] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [syncDetails, setSyncDetails] = useState(null);

  // Fetch last backup info on mount
  useEffect(() => {
    const fetchLastBackup = async () => {
      try {
        const response = await fetch(`${backend_url}/admin/list-backups`, {
          method: "GET",
          headers: {
            "auth-token": localStorage.getItem("auth-token"),
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.backups && data.backups.length > 0) {
          const latest = data.backups[0];
          setLastBackup({
            filename: latest.filename,
            date: new Date(latest.created).toLocaleString(),
            admin: latest.filename.match(/fashionspice_backup_.*\.zip/)
              ? undefined // admin name is not in filename, will show in backupInfo after backup
              : undefined,
          });
        }
      } catch (e) {
        // ignore
      }
    };
    fetchLastBackup();
  }, []);

  // Handle database backup
  const handleBackup = async () => {
    setBackupLoading(true);
    setBackupStatus("Creating database backup...");

    try {
      const response = await fetch(`${backend_url}/admin/backup-database`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": localStorage.getItem("auth-token"),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setBackupStatus("Backup created successfully! Downloading...");

        // Download the backup file
        const downloadResponse = await fetch(
          `${backend_url}/admin/download-backup/${data.filename}`,
          {
            method: "GET",
            headers: {
              "auth-token": localStorage.getItem("auth-token"),
            },
          }
        );

        if (!downloadResponse.ok) {
          throw new Error("Failed to download backup file");
        }

        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setBackupStatus(
          `Backup completed successfully! File: ${data.filename}`
        );
        setBackupInfo(data.info);
        setLastBackup({
          filename: data.filename,
          date: new Date(data.info.timestamp).toLocaleString(),
          admin: data.info.createdBy || "Unknown",
        });
      } else {
        throw new Error(data.message || "Backup failed");
      }
    } catch (error) {
      console.error("Backup error:", error);
      setBackupStatus(`Backup failed: ${error.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  // Handle file selection for restore
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.endsWith(".zip")) {
        setSelectedFile(file);
        setRestoreStatus(`Selected file: ${file.name}`);
      } else {
        setRestoreStatus("Please select a valid backup file (.zip)");
        setSelectedFile(null);
      }
    }
  };

  // Handle database restore
  const handleRestore = async () => {
    if (!selectedFile) {
      setRestoreStatus("Please select a backup file first");
      return;
    }

    setRestoreLoading(true);
    setRestoreStatus("Uploading backup file...");

    try {
      const formData = new FormData();
      formData.append("backup", selectedFile);

      // Create an AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6 * 60 * 1000); // 6 minutes timeout

      const response = await fetch(`${backend_url}/admin/restore-database`, {
        method: "POST",
        headers: {
          "auth-token": localStorage.getItem("auth-token"),
        },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle specific HTTP status codes
        if (response.status === 502) {
          throw new Error("Server is temporarily unavailable (502 Bad Gateway). Please try again in a few minutes.");
        }
        if (response.status === 408) {
          throw new Error("Restore operation timed out. Please try again with a smaller backup file.");
        }
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Invalid backup file. Please check the file format.");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setRestoreStatus(`Database restored successfully! ${data.message}`);
        setSelectedFile(null);
        // Clear the file input
        const fileInput = document.getElementById("restore-file");
        if (fileInput) fileInput.value = "";
        // Show last restore info
        if (data.backupInfo) {
          setLastRestore({
            date: new Date(data.backupInfo.timestamp).toLocaleString(),
            admin: data.backupInfo.createdBy || "Unknown",
          });
        }
      } else {
        throw new Error(data.message || "Restore failed");
      }
    } catch (error) {
      console.error("Restore error:", error);
      if (error.name === 'AbortError') {
        setRestoreStatus("Restore operation timed out. Please try again.");
      } else {
        setRestoreStatus(`Restore failed: ${error.message}`);
      }
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handle one-click sync of images Render ↔ GitHub
  const handleSyncImages = async () => {
    setSyncLoading(true);
    setSyncStatus("Synchronizing images between Render and GitHub...");
    setSyncDetails(null);
    try {
      const response = await fetch(`${backend_url}/admin/sync-upload-images`, {
        method: "POST",
        headers: {
          "auth-token": localStorage.getItem("auth-token"),
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setSyncStatus(
        `Sync complete. Pulled: ${data.details.pulledFromGitHub}, MissingLocal: ${data.details.missingLocal}, MissingRemote: ${data.details.missingRemote}`
      );
      setSyncDetails(data.details);
    } catch (e) {
      setSyncStatus(`Sync failed: ${e.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Handle backup file test

  return (
    <div className="database-backup-container">
      <div className="backup-header">
        <h1>Database Backup & Restore</h1>
        <p>Backup your database to local disk or restore from a backup file</p>
        <div className="backup-restore-meta">
          {lastBackup && (
            <div className="meta-info">
              <strong>Last Successful Backup:</strong>
              <br />
              File: {lastBackup.filename}
              <br />
              Date: {lastBackup.date}
              {lastBackup.admin && (
                <>
                  <br />
                  By: {lastBackup.admin}
                </>
              )}
            </div>
          )}
          {lastRestore && (
            <div className="meta-info">
              <strong>Last Successful Restore:</strong>
              <br />
              Date: {lastRestore.date}
              <br />
              By: {lastRestore.admin}
            </div>
          )}
        </div>
      </div>

      <div className="backup-sections">
        {/* Backup Section */}
        <div className="backup-section">
          <div className="section-header">
            <h2>📦 Create Backup</h2>
            <p>
              Export all database collections and product images to a
              downloadable backup file
            </p>
          </div>

          <div className="backup-controls">
            <button
              className="backup-btn"
              onClick={handleBackup}
              disabled={backupLoading}
            >
              {backupLoading ? "Creating Backup..." : "Create Database Backup"}
            </button>
          </div>

          {backupStatus && (
            <div
              className={`status-message ${
                backupStatus.includes("failed") ? "error" : "success"
              }`}
            >
              {backupStatus}
            </div>
          )}

          {backupInfo && (
            <div className="backup-info">
              <h3>Backup Information:</h3>
              <ul>
                <li>Products: {backupInfo.products} records</li>
                <li>Users: {backupInfo.users} records</li>
                <li>Orders: {backupInfo.orders} records</li>
                <li>Admin Users: {backupInfo.admins} records</li>
                <li>
                  Newsletter Subscribers: {backupInfo.newsletters} records
                </li>
                <li>Reviews: {backupInfo.reviews} records</li>
                <li>Images: {backupInfo.images || 0} files</li>
                <li>
                  Created: {new Date(backupInfo.timestamp).toLocaleString()}
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Sync Section */}
        <div className="sync-section">
          <div className="section-header">
            <h2>🔄 Sync Images (Render ↔ GitHub)</h2>
            <p>
              Ensure images under `upload/images` are identical on both sides
            </p>
          </div>
          <div className="sync-controls">
            <button
              className="backup-btn"
              onClick={handleSyncImages}
              disabled={syncLoading}
            >
              {syncLoading ? "Syncing..." : "Sync Images Now"}
            </button>
          </div>
          {syncStatus && (
            <div
              className={`status-message ${
                syncStatus.includes("failed") ? "error" : "success"
              }`}
            >
              {syncStatus}
            </div>
          )}
          {syncDetails && (
            <div className="backup-info" style={{ marginTop: 12 }}>
              <h3>Sync Details</h3>
              <pre style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}>
                {JSON.stringify(syncDetails, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Restore Section */}
        <div className="restore-section">
          <div className="section-header">
            <h2>📥 Restore Database</h2>
            <p>Import data from a backup file to restore your database</p>
          </div>

          <div className="restore-controls">
            <div className="file-input-container">
              <label htmlFor="restore-file" className="file-label">
                Choose Backup File (.zip)
              </label>
              <input
                id="restore-file"
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="file-input"
              />
            </div>

            <button
              className="restore-btn"
              onClick={handleRestore}
              disabled={restoreLoading || !selectedFile}
              style={{
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {restoreLoading ? "Restoring..." : "Restore Database"}
            </button>
          </div>

          {restoreStatus && (
            <div
              className={`status-message ${
                restoreStatus.includes("failed") ? "error" : "success"
              }`}
            >
              {restoreStatus}
            </div>
          )}

          <div className="restore-warning">
            <h3>⚠️ Warning:</h3>
            <ul>
              <li>Restore will replace all existing data in the database</li>
              <li>This operation cannot be undone</li>
              <li>It's recommended to create a backup before restoring</li>
              <li>Only restore backup files created by this system</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="help-section">
        <h3>📖 How to Use:</h3>
        <div className="help-content">
          <div className="help-item">
            <h4>Creating a Backup:</h4>
            <p>
              Click "Create Database Backup" to export all your data. The backup
              file will be automatically downloaded to your computer.
            </p>
          </div>
          <div className="help-item">
            <h4>Restoring from Backup:</h4>
            <p>
              Select a backup file (.zip) and click "Restore Database". This
              will replace all current data with the backup data.
            </p>
          </div>
          <div className="help-item">
            <h4>Backup Contents:</h4>
            <p>
              Backups include: Products, Users, Orders, Admin Users, Newsletter
              Subscribers, Reviews, and all product images with their original
              filenames.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseBackup;
